import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    MdAutoAwesome,
    MdBusiness,
    MdChatBubbleOutline,
    MdCheckCircle,
    MdCode,
    MdEdit,
    MdEvent,
    MdPauseCircle,
    MdPlayArrow,
    MdStop,
    MdSupportAgent,
    MdTune,
    MdUpdate,
} from 'react-icons/md';
import { toast } from 'react-toastify';
import { useAuth } from '../../../Context/AuthContext';
import { PermissionCodes, isSuperAdminUser, permissionHas, resolveUserPermissionCodes } from '../../../Utils/Permissions';
import { getAegisAiAssistConfigById, getAegisAiAssistIconUrl } from '../../../Services/AegisAiAssistService';
import { APPRoutes } from '../../../Utils/Route';
import { formatUtcToLocalDateTime } from '../../../Utils/DateTime';
import ViewEmptyState from '../../../Components/Common/ViewEmptyState';
import { getAssistIconComponent, getAssistIconLabel, getAssistPositionLabel } from '../../../Utils/AssistIcons';
import { AiCard, AiStatusPill, AiViewSkeleton } from '../AegisAiUi';
import BackButton from '../../../Components/Common/BackButton';
import { AssistEmbedCode } from './AssistEmbedCode';
import AssistTrialWidget from './AssistTrialWidget';

const DEFAULT_COLOR = '#1c4fa6';


const DEFAULT_FALLBACK_TEXT = "Default: \"I don't have knowledge in this area. Please connect with your client administrator for further assistance.\"";

// One configuration value: a small visual (swatch or icon) beside its label and value.
function Setting({ label, visual, children }) {
    return (
        <div className="hc-ai-agent-setting d-flex align-items-center gap-2">
            <span className="flex-shrink-0 d-inline-flex">{visual}</span>
            <div style={{ minWidth: 0 }}>
                <div className="hc-ai-agent-setting-label">{label}</div>
                <div className="hc-ai-agent-setting-value">{children}</div>
            </div>
        </div>
    );
}

const STATUS_STYLE = {
    Active: { icon: MdCheckCircle, tone: 'success' },
    Inactive: { icon: MdPauseCircle, tone: 'inactive' },
    Unknown: { icon: MdPauseCircle, tone: 'inactive' },
};

export default function AegisAiAssistView() {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const { token, permissions, decodedToken } = useAuth();
    const userPermissionCodes = React.useMemo(
        () => resolveUserPermissionCodes({ permissions, decodedToken }),
        [permissions, decodedToken]
    );

    const [config, setConfig] = React.useState(location.state?.assistConfig || null);
    const [loading, setLoading] = React.useState(!location.state?.assistConfig);
    const [trialState, setTrialState] = React.useState('off'); // off | on | exiting
    const canEditConfig = permissionHas(userPermissionCodes, PermissionCodes.AGENT_BUILDER_EDIT, decodedToken);
    const canViewList = permissionHas(userPermissionCodes, [PermissionCodes.AGENT_BUILDER_LIST, PermissionCodes.AGENT_BUILDER_VIEW], decodedToken);
    const canViewConfig = permissionHas(userPermissionCodes, PermissionCodes.AGENT_BUILDER_VIEW, decodedToken);
    const isSuperAdmin = isSuperAdminUser(decodedToken);

    React.useEffect(() => {
        if (!token || location.state?.assistConfig) return;
        (async () => {
            try {
                setLoading(true);
                const response = await getAegisAiAssistConfigById(id, token);
                if (response?.is_success) {
                    setConfig(response?.data || null);
                } else if (response?.is_success === false && response?.status_code === 500) {
                    toast.error('Internal server error. Please Contact Support Team.');
                    setConfig(null);
                } else {
                    toast.error(response?.message || 'Failed to load agent details');
                    setConfig(null);
                }
            } catch (error) {
                console.error(error);
                setConfig(null);
                toast.error('An error occurred while loading agent details');
            } finally {
                setLoading(false);
            }
        })();
    }, [id, token, location.state?.assistConfig]);

    const handleTrialExited = React.useCallback(() => setTrialState('off'), []);

    if (!canViewConfig) {
        return (
            <div className="container-fluid py-4">
                <div className="alert alert-danger">You do not have permission to view this agent.</div>
            </div>
        );
    }

    if (loading) {
        return <AiViewSkeleton />;
    }

    if (!config) {
        return (
            <ViewEmptyState
                title="No Agents Available"
                description="There are no agent configurations available to display."
                icon={MdSupportAgent}
                canViewList={canViewList}
                backRoute={APPRoutes.AEGIS_AI_ASSIST}
                backLabel="Back to Agent Builder"
            />
        );
    }

    const statusName = config?.Status || 'Unknown';
    const statusMeta = STATUS_STYLE[statusName] || STATUS_STYLE.Unknown;
    const logoUrl = getAegisAiAssistIconUrl(config?.Id, config?.IconFileId);
    // The preset icon is the floating launcher; the uploaded logo is shown inside the chat window.
    const AgentIcon = getAssistIconComponent(config?.Icon);
    const AgentLogo = getAssistIconComponent(config?.Icon, logoUrl);
    const createdOn = formatUtcToLocalDateTime(config?.CreatedOn);
    const modifiedOn = formatUtcToLocalDateTime(config?.ModifiedOn);
    const agentColor = config?.PrimaryColor || DEFAULT_COLOR;
    const positionValue = config?.Position || config?.position || 'bottom-right';
    const isActive = statusName === 'Active';

    return (
        <div className="container-fluid py-4 hc-ai-page hc-ai-agent-view">
            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                {canViewList ? <BackButton onClick={() => navigate(APPRoutes.AEGIS_AI_ASSIST)} /> : <span />}
                <div className="d-flex flex-wrap gap-2">
                    {isActive ? (
                        <button
                            type="button"
                            className="hc-ai-btn overflow-hidden d-inline-flex align-items-center justify-content-center gap-2 fw-bold text-nowrap hc-ai-btn-outline"
                            onClick={() => setTrialState((state) => (state === 'on' ? 'exiting' : 'on'))}
                            disabled={trialState === 'exiting'}
                        >
                            {trialState === 'on' ? <MdStop size={17} /> : <MdPlayArrow size={18} />}
                            <span>{trialState === 'on' ? 'End Trial' : 'Try it live'}</span>
                        </button>
                    ) : null}
                    {canEditConfig ? (
                        <button type="button" className="hc-ai-btn overflow-hidden d-inline-flex align-items-center justify-content-center gap-2 fw-bold text-nowrap hc-ai-btn-primary" onClick={() => navigate(APPRoutes.AEGIS_AI_ASSIST_EDIT.replace(':id', config?.Id))}>
                            <MdEdit size={17} />
                            <span>Edit Agent</span>
                        </button>
                    ) : null}
                </div>
            </div>

            <section className="hc-ai-agent-hero position-relative overflow-hidden text-white mb-4">
                <span aria-hidden="true" className="hc-ai-agent-hero-ring hc-ai-agent-hero-ring-a" />
                <span aria-hidden="true" className="hc-ai-agent-hero-ring hc-ai-agent-hero-ring-b" />
                <div className="row g-4 align-items-center position-relative">
                    <div className="col-12">
                        <div className="d-flex align-items-start gap-3 gap-md-4">
                            <span className="hc-ai-agent-avatar d-inline-flex align-items-center justify-content-center flex-shrink-0">
                                <AgentLogo size={34} />
                            </span>
                            <div style={{ minWidth: 0 }}>
                                <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                    <span className="hc-ai-agent-eyebrow d-inline-flex align-items-center gap-1 rounded-pill fw-bold text-uppercase">
                                        <MdAutoAwesome size={13} />
                                        AI Agent
                                    </span>
                                    <AiStatusPill tone={statusMeta.tone} label={statusName} icon={statusMeta.icon} large />
                                </div>
                                <h2 className="hc-ai-agent-title fw-bold mb-1">{config?.Title || '-'}</h2>
                                <p className="hc-ai-agent-sub m-0">{config?.Description || 'No description provided for this agent.'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <AiCard className="h-auto mb-4" icon={MdTune} title="Configuration" subtitle="Everything this agent is set up with">
                <div className="hc-ai-agent-config d-grid">
                    <div className="hc-ai-browser rounded-4 bg-white overflow-hidden align-self-start" style={{ '--widget-color': agentColor }}>
                        <div className="hc-ai-browser-bar align-items-center" aria-hidden="true">
                            <i />
                            <i />
                            <i />
                            <span className="hc-ai-browser-url ms-2 rounded-2 bg-white" />
                        </div>
                        <div className={`hc-ai-browser-stage is-compact hc-ai-stage-${positionValue} position-relative p-3`}>
                            <div aria-hidden="true">
                                <div className="hc-ai-skel-line rounded-pill" style={{ width: '46%', height: 12 }} />
                                <div className="hc-ai-skel-line rounded-pill" style={{ width: '72%' }} />
                            </div>
                            <div className={`hc-ai-widget pos-${positionValue} position-absolute rounded-3 bg-white overflow-hidden`}>
                                <div className="hc-ai-widget-head d-flex align-items-center text-white">
                                    <span className="hc-ai-widget-head-icon d-inline-flex align-items-center justify-content-center flex-shrink-0">
                                        <AgentLogo size={13} />
                                    </span>
                                    <span className="fw-semibold text-truncate" style={{ fontSize: 11 }}>{config?.Title || 'Agent'}</span>
                                </div>
                                <div className="hc-ai-widget-body d-flex flex-column gap-1">
                                    <div className="hc-ai-widget-bubble">{config?.Description || 'Hi.'}</div>
                                    <div className="hc-ai-widget-bubble hc-ai-widget-bubble-user align-self-end">Hi there!</div>
                                </div>
                                <div className="hc-ai-widget-input rounded-pill" />
                            </div>
                            <span className={`hc-ai-launcher pos-${positionValue} position-absolute rounded-circle d-inline-flex align-items-center justify-content-center text-white`}>
                                <AgentIcon size={18} />
                            </span>
                        </div>
                    </div>

                    <div className="hc-ai-agent-settings d-grid">
                        <Setting label="Color" visual={<span className="hc-ai-agent-setting-chip d-inline-block" style={{ backgroundColor: agentColor }} />}>
                            {config?.PrimaryColor || '-'}
                        </Setting>
                        <Setting label="Launcher Icon" visual={<span className="hc-ai-agent-setting-chip is-theme d-inline-flex align-items-center justify-content-center text-white"><AgentIcon size={16} /></span>}>
                            {getAssistIconLabel(config?.Icon)}
                        </Setting>
                        <Setting label="Logo" visual={<span className="hc-ai-agent-setting-chip is-theme d-inline-flex align-items-center justify-content-center text-white"><AgentLogo size={16} /></span>}>
                            {logoUrl ? 'Uploaded' : 'Not uploaded'}
                        </Setting>
                        <Setting
                            label="Position"
                            visual={(
                                <span className="hc-ai-agent-setting-chip is-theme d-inline-flex align-items-center justify-content-center">
                                    <span className="hc-ai-position-icon-box position-relative" style={{ width: 18, height: 14, borderColor: 'rgba(255,255,255,0.75)', background: 'transparent' }}>
                                        <span className={`hc-ai-position-dot hc-ai-pos-dot-${positionValue}`} style={{ backgroundColor: '#ffffff', width: 4, height: 4 }} />
                                    </span>
                                </span>
                            )}
                        >
                            {getAssistPositionLabel(positionValue)}
                        </Setting>
                        <Setting label="Client" visual={<span className="hc-ai-agent-setting-chip is-soft d-inline-flex align-items-center justify-content-center"><MdBusiness size={16} /></span>}>
                            {config?.ClientName || '-'}
                        </Setting>
                        <Setting label="Created" visual={<span className="hc-ai-agent-setting-chip is-soft d-inline-flex align-items-center justify-content-center"><MdEvent size={16} /></span>}>
                            {createdOn || '-'}{config?.CreatedBy ? <span className="hc-ai-agent-setting-by"> by {config.CreatedBy}</span> : null}
                        </Setting>
                        <Setting label="Last updated" visual={<span className="hc-ai-agent-setting-chip is-soft d-inline-flex align-items-center justify-content-center"><MdUpdate size={16} /></span>}>
                            {modifiedOn || createdOn || '-'}{config?.ModifiedBy ? <span className="hc-ai-agent-setting-by"> by {config.ModifiedBy}</span> : null}
                        </Setting>

                        <div className="hc-ai-agent-setting is-wide">
                            <div className="hc-ai-agent-setting-label d-flex align-items-center gap-1 mb-2"><MdChatBubbleOutline size={13} /> Fallback message</div>
                            <blockquote className={`hc-ai-agent-quote m-0 ${config?.FallbackMessage ? '' : 'is-default'}`}>
                                {config?.FallbackMessage || DEFAULT_FALLBACK_TEXT}
                            </blockquote>
                            <div className="hc-ai-agent-setting-hint">Shown when the knowledge base has no answer or the reply takes too long.</div>
                        </div>
                    </div>
                </div>
            </AiCard>

            {config?.Id ? (
                <AiCard className="h-auto" icon={MdCode} title="Add to a website" subtitle="Three steps to put this agent on the client's site">
                    <AssistEmbedCode agentId={config.Id} title={config.Title} />
                </AiCard>
            ) : null}

            {trialState !== 'off' ? (
                <AssistTrialWidget
                    token={token}
                    clientId={isSuperAdmin ? config?.ClientId : undefined}
                    title={config?.Title || 'AI Assistant'}
                    description={config?.Description}
                    primaryColor={agentColor}
                    icon={AgentIcon}
                    logo={AgentLogo}
                    position={positionValue}
                    fallbackMessage={config?.FallbackMessage || ''}
                    exiting={trialState === 'exiting'}
                    onExited={handleTrialExited}
                />
            ) : null}
        </div>
    );
}
